'use strict';

import Vue from 'vue';
import actions from './actions';
import getters from './getters';
import mutations from './mutations';
import { STATS_SORTING } from '@/constants/businessProcess/constants';
import { COLUMN_TYPE } from '@/constants/table';

export const defaultSupervisorSummaryTable = [
  {
    text: 'headers.businessProcess',
    align: 'start',
    sortable: true,
    value: 'businessProcessName',
    type: COLUMN_TYPE.STRING,
    filterable: true,
    hide: false,
    columnIndex: 0
  },
  {
    text: 'headers.totalQueuedInteractions',
    align: 'start',
    sortable: true,
    value: 'businessProcessTotalQueuedInteractions',
    type: COLUMN_TYPE.NUMBER,
    filterable: true,
    hide: false,
    columnIndex: 1
  },
  {
    text: 'headers.totalAnswered',
    align: 'start',
    sortable: true,
    value: 'businessProcessAnsweredInteractions',
    type: COLUMN_TYPE.NUMBER,
    filterable: true,
    hide: false,
    columnIndex: 2
  },
  {
    text: 'headers.totalAbandoned',
    align: 'start',
    sortable: true,
    value: 'businessProcessAbandonedInteractions',
    type: COLUMN_TYPE.NUMBER,
    filterable: true,
    hide: false,
    columnIndex: 3
  },
  {
    text: 'headers.totalOverflowed',
    align: 'start',
    sortable: true,
    value: 'businessProcessOverflowInteractions',
    type: COLUMN_TYPE.NUMBER,
    filterable: true,
    hide: false,
    columnIndex: 4
  },
  {
    text: 'headers.totalCallbackRequested',
    align: 'start',
    sortable: true,
    value: 'callbackRequestedInShift',
    type: COLUMN_TYPE.NUMBER,
    filterable: true,
    hide: false,
    columnIndex: 5
  },
  {
    text: 'headers.totalCallbacksProcessed',
    align: 'start',
    sortable: true,
    value: 'callbackProcessedInShift',
    type: COLUMN_TYPE.NUMBER,
    filterable: true,
    hide: false,
    columnIndex: 6
  },
  {
    text: 'headers.totalCallbacksToBeProcessed',
    align: 'start',
    sortable: true,
    value: 'callbackToBeProcessed',
    type: COLUMN_TYPE.NUMBER,
    filterable: true,
    hide: false,
    columnIndex: 7
  },
  {
    text: 'headers.serviceLevel',
    align: 'end',
    sortable: true,
    value: 'businessProcessServiceLevel',
    type: COLUMN_TYPE.PERCENTAGE,
    filterable: true,
    hide: false,
    columnIndex: 8
  },
  {
    text: 'headers.answerRate',
    align: 'end',
    sortable: true,
    value: 'businessProcessAnsweredPercentage',
    type: COLUMN_TYPE.PERCENTAGE,
    filterable: true,
    hide: false,
    columnIndex: 9
  },
  {
    text: 'headers.abandonRate',
    align: 'end',
    sortable: true,
    value: 'businessProcessAbandonedPercentage',
    type: COLUMN_TYPE.PERCENTAGE,
    filterable: true,
    hide: false,
    columnIndex: 10
  },
  {
    text: 'headers.asa',
    align: 'end',
    sortable: true,
    value: 'businessProcessASA',
    type: COLUMN_TYPE.TIME,
    filterable: false,
    hide: false,
    columnIndex: 11
  }
];

export const defaultSupervisorSummaryTableSettings = {
  hiddenBusinessProcessesIds: [],
  sortedBusinessProcessesIds: []
};

export const defaultSettings = {
  preferencesSettings: {
    incomingInteractionDisplay: 'bannerAndPanel',
    autoPlayRecordings: false,
    autoOffHookState: true,
    autoOpenInteractionDetails: false,
    pauseKPIEvents: false,
    muteDTMF: false,
    incomingInteractionRingtones: {
      call: 'silent',
      chat: 'silent',
      email: 'silent'
    },
    interactionHistoryPeriod: 168,
    idleTimeToAgentIntelligenceCenter: 300,
    alertEndOfBreak: 0,
    showAppCurrentTime: false
  },
  applicationSettings: {
    language: 'en',
    timeFormat: 'hh:mm A',
    timeZoneId: 1
  },
  audioSettings: {
    speakerVolume: 0,
    microphoneVolume: 0
  },
  agentSettings: {
    defaultOutgoingCallBusinessProcessId: null,
    defaultOutgoingEmailBusinessProcessId: null
  },
  agentState: {
    lastSelectedTerminal: {}
  },
  theme: {
    mode: 'light',
    _id: '5f04ccd25fb50dccdd53d276'
  },
  featuresSettings: {
    showApplicationTour: false,
    latestShowedFeatures: null,
    pinnedSideBarOnMaximizedMode: false // NOTE: It's applied to supervisor role only
  },
  dashboardSettings: {
    selectedDashboardIndex: -1,
    dashboards: []
  },
  supervisorDashboardSettings: {
    adaptiveDashboards: {
      sorting: STATS_SORTING.Health,
      cards: []
    },
    summaryTable: defaultSupervisorSummaryTable,
    summaryTableSettings: defaultSupervisorSummaryTableSettings
  },
  supervisorWallboardSettings: {
    selectedWallboardIndex: -1,
    wallboards: [{ name: 'Default Wallboard' }]
  }
};

const defaultConnectionSettings = {
  offlineMode: false
};

const defaultSoftPhoneSettings = {
  debug: true,
  enabled: false,
  sipRegistrar: 'sip:voip.lab.evolveip.net:5060',
  sipUserName: ''
};

const state = {
  currentUserSettings: defaultSettings,
  connectionSettings: defaultConnectionSettings,
  softPhoneSettings: defaultSoftPhoneSettings
};

// eslint-disable-next-line import/no-named-as-default-member
Vue.set(state, 'currentUserSettings', defaultSettings);

export default {
  namespaced: true,

  /**
   * As a best practice: Actions call mutations which update our current state
   * Actions could execute sync or async operations.
   */
  actions,

  /**
   * Access to state values
   */
  getters,

  /**
   * Used to commit and track state changes
   * Mutations should always execute sync operations.
   */
  mutations,

  /**
   * Single source of truth
   */
  state
};
